import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeploymentGroup, useCreateDeploymentGroup, useUpdateDeploymentGroup } from '../../services/queries';
import { LoadingSpinner } from '../common';
import { ErrorAlert } from '../ErrorAlert';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { CreateDeploymentGroupDto, UpdateDeploymentGroupDto } from '../../types/pipelineFlow';

interface DeploymentGroupFormProps {
  mode: 'create' | 'edit';
}

export function DeploymentGroupForm({ mode }: DeploymentGroupFormProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorHandler = useErrorHandler();

  // Hooks for data fetching and mutations
  const { data: existingGroup, isLoading: loadingGroup } = useDeploymentGroup(id!, mode === 'edit');
  const createMutation = useCreateDeploymentGroup();
  const updateMutation = useUpdateDeploymentGroup();

  // Load existing data for edit mode
  useEffect(() => {
    if (mode === 'edit' && existingGroup) {
      setName(existingGroup.name);
    }
  }, [mode, existingGroup]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Clear any previous errors
      errorHandler.clearError();
      
      if (mode === 'create') {
        const dto: CreateDeploymentGroupDto = { name: name.trim() };
        await createMutation.mutateAsync(dto);
        navigate('/admin/deployment-groups');
      } else if (mode === 'edit' && id) {
        const dto: UpdateDeploymentGroupDto = { name: name.trim() };
        await updateMutation.mutateAsync({ id, group: dto });
        navigate(`/admin/deployment-groups/${id}`);
      }
    } catch (error) {
      console.error('Failed to save deployment group:', error);
      errorHandler.showError(error as Error);
    }
  };

  const handleCancel = () => {
    if (mode === 'edit' && id) {
      navigate(`/admin/deployment-groups/${id}`);
    } else {
      navigate('/admin/deployment-groups');
    }
  };

  // Show loading spinner while fetching data for edit mode
  if (mode === 'edit' && loadingGroup) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const mutation = mode === 'create' ? createMutation : updateMutation;
  const isSubmitting = mutation.isPending;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold leading-7 text-gray-900">
            {mode === 'create' ? 'Create Deployment Group' : 'Edit Deployment Group'}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {mode === 'create' 
              ? 'Create a new deployment group to organize related pipelines.'
              : 'Update the deployment group information.'
            }
          </p>
        </div>

        {/* Error Alert */}
        <ErrorAlert 
          error={errorHandler.error} 
          isVisible={errorHandler.isVisible} 
          onClose={errorHandler.hideError}
          className="mb-6"
        />

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
            <div className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger-500">*</span>
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`form-input ${
                        errors.name
                          ? 'ring-danger-300 focus:ring-danger-500'
                          : ''
                      }`}
                      placeholder="Enter deployment group name"
                      disabled={isSubmitting}
                    />
                    {errors.name && (
                      <p className="form-error">{errors.name}</p>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    A descriptive name for the deployment group (e.g., "Production Web Services", "Development APIs").
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-x-6">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="btn-primary flex items-center"
            >
              {isSubmitting && <LoadingSpinner size="sm" className="mr-2" />}
              {mode === 'create' ? 'Create Group' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}